<template>
    <div class="row g-3">
        <!-- NAGŁÓWEK ZAKŁADKI -->
        <div class="col-12 d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center gap-2 border-bottom pb-2 mb-2">
            <div>
                <h6 class="fw-bold mb-0">Banner główny oraz Meta Tagi strony</h6>
                <small class="text-muted">Zarządzanie sekcją nagłówkową na stronie głównej oraz ustawieniami SEO</small>
            </div>
            <button type="button" class="btn btn-primary-dark d-flex align-items-center gap-2"
                :disabled="loading || saving" @click="saveData">
                <i class="bi bi-floppy"></i>
                <span>Zapisz zmiany</span>
            </button>
        </div>

        <!-- SEKCJA 1: BANNER GŁÓWNY -->
        <div class="col-12 mt-2">
            <h6 class="fw-bold text-primary mb-3">
                <i class="bi bi-image me-1"></i> Banner główny (1920 x 400 px)
            </h6>
        </div>

        <!-- PODGLĄD BANNERA -->
        <div class="col-12">
            <label class="form-label fw-semibold">Podgląd aktualnego bannera</label>
            <div
                class="banner-preview-container rounded border bg-light d-flex align-items-center justify-content-center overflow-hidden position-relative">
                <img v-if="form.bannerUrl" :src="form.bannerUrl" alt="Banner główny" class="img-fluid banner-img" />
                <div v-else class="text-center text-muted p-4">
                    <i class="bi bi-card-image fs-1 d-block mb-2"></i>
                    <span>Brak wgranego bannera</span>
                </div>
            </div>
            <div class="form-text mt-1">Zalecany format: <strong>1920 x 400 px</strong> (JPG, PNG, WebP, max 6MB).</div>
        </div>

        <!-- WGIRANIE BANNERA -->
        <div class="col-12 col-md-6">
            <label class="form-label fw-semibold">Wgraj nowy banner</label>
            <input type="file" class="form-control" accept="image/jpeg,image/png,image/webp"
                :disabled="loading || saving" @change="handleFileSelect" />
        </div>

        <div class="col-12 col-md-6">
            <label class="form-label fw-semibold">Link po kliknięciu w banner (opcjonalnie)</label>
            <input type="url" class="form-control" v-model="form.bannerLink" :disabled="loading"
                placeholder="https://domena.pl/promocja" />
        </div>

        <div class="col-12">
            <label class="form-label fw-semibold">Tekst alternatywny bannera (Alt Text)</label>
            <input type="text" class="form-control" v-model="form.bannerAlt" :disabled="loading"
                placeholder="np. Baner promocyjny - wyprzedaż rocznika" />
        </div>

        <!-- SEKCJA 2: META TAGI SEO -->
        <div class="col-12 mt-4">
            <h6 class="fw-bold text-primary mb-3">
                <i class="bi bi-search me-1"></i> Meta Tagi strony (SEO)
            </h6>
        </div>

        <!-- TITLE -->
        <div class="col-12">
            <div class="d-flex justify-content-between align-items-center">
                <label class="form-label fw-semibold mb-1">Tytuł strony (Meta Title)</label>
                <small :class="titleCounterClass">
                    {{ form.metaTitle.length }} / 60 znaków
                </small>
            </div>
            <input type="text" class="form-control" v-model="form.metaTitle" :disabled="loading" maxlength="80"
                placeholder="np. Skup i sprzedaż samochodów używanych | NazwaFirmy" />
            <div class="form-text">Optymalna długość dla Google to <strong>50 - 60 znaków</strong>.</div>
        </div>

        <!-- DESCRIPTION -->
        <div class="col-12 mt-2">
            <div class="d-flex justify-content-between align-items-center">
                <label class="form-label fw-semibold mb-1">Opis strony (Meta Description)</label>
                <small :class="descriptionCounterClass">
                    {{ form.metaDescription.length }} / 160 znaków
                </small>
            </div>
            <textarea class="form-control" rows="3" v-model="form.metaDescription" :disabled="loading" maxlength="200"
                placeholder="np. Sprawdź naszą ofertę sprawdzonych aut używanych z gwarancją. Oferujemy atrakcyjne ceny, pełną historię pojazdu i szybki proces zakupu."></textarea>
            <div class="form-text">Optymalna długość dla wyników wyszukiwania to <strong>150 - 160 znaków</strong>.
            </div>
        </div>

        <!-- KEYWORDS -->
        <div class="col-12 mt-2">
            <label class="form-label fw-semibold">Słowa kluczowe (Meta Keywords)</label>
            <input type="text" class="form-control" v-model="form.metaKeywords" :disabled="loading"
                placeholder="np. samochody używane, skup aut, tanie auta, komis samochodowy" />
            <div class="form-text">Wpisz słowa kluczowe rozdzielone przecinkami (3 - 8 najważniejszych fraz).</div>
        </div>

        <!-- DOLNY PRZYCISK ZAPISU -->
        <div class="col-12 d-flex justify-content-end mt-4 pt-3 border-top">
            <button type="button" class="btn btn-primary-dark d-flex align-items-center gap-2"
                :disabled="loading || saving" @click="saveData">
                <i class="bi bi-floppy"></i>
                <span>Zapisz zmiany</span>
            </button>
        </div>
    </div>

    <!-- LOADER -->
    <AppLoader :show="loading || saving" :text="loaderText" />
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import apiClient from '@/api'
import AppLoader from '@/components/AppLoader.vue'
import { webAlert } from '@/utils/dialog'

const loading = ref(false)
const saving = ref(false)
const loaderText = ref('')
const selectedFile = ref(null)

const form = ref({
    bannerUrl: '',
    bannerLink: '',
    bannerAlt: '',
    metaTitle: '',
    metaDescription: '',
    metaKeywords: ''
})

// Liczniki znaków dla SEO
const titleCounterClass = computed(() => {
    const len = form.value.metaTitle.length
    if (len >= 50 && len <= 60) return 'text-success fw-bold'
    if (len > 60) return 'text-danger fw-bold'
    return 'text-muted'
})

const descriptionCounterClass = computed(() => {
    const len = form.value.metaDescription.length
    if (len >= 150 && len <= 160) return 'text-success fw-bold'
    if (len > 160) return 'text-danger fw-bold'
    return 'text-muted'
})

const handleFileSelect = (event) => {
    const file = event.target.files[0]
    if (!file) return

    if (file.size > 6 * 1024 * 1024) {
        webAlert('Plik jest za duży. Maksymalny rozmiar bannera to 2 MB.')
        event.target.value = ''
        return
    }

    selectedFile.value = file
    // Podgląd lokalny przed wysłaniem na serwer
    form.value.bannerUrl = URL.createObjectURL(file)
}

const loadData = async () => {
    loading.value = true
    loaderText.value = 'Pobieranie ustawień bannera i SEO...'

    try {
        const response = await apiClient.post(
            { action: 'get-settings-banner-meta' }
        )

        if (response.data?.data) {
            form.value.bannerUrl = response.data.data.bannerUrl || ''
            form.value.bannerLink = response.data.data.bannerLink || ''
            form.value.bannerAlt = response.data.data.bannerAlt || ''
            form.value.metaTitle = response.data.data.metaTitle || ''
            form.value.metaDescription = response.data.data.metaDescription || ''
            form.value.metaKeywords = response.data.data.metaKeywords || ''
        }
    } catch (error) {
        webAlert('Błąd podczas pobierania danych bannera i SEO.')
    } finally {
        loading.value = false
    }
}

const saveData = async () => {
    saving.value = true
    loaderText.value = 'Zapisywanie ustawień...'

    try {
        // Używamy FormData na wypadek przesyłania pliku obrazu
        const formData = new FormData()
        formData.append('action', 'save-settings-banner-meta')
        formData.append('bannerLink', form.value.bannerLink)
        formData.append('bannerAlt', form.value.bannerAlt)
        formData.append('metaTitle', form.value.metaTitle)
        formData.append('metaDescription', form.value.metaDescription)
        formData.append('metaKeywords', form.value.metaKeywords)

        if (selectedFile.value) {
            formData.append('bannerFile', selectedFile.value)
        }

        const response = await apiClient.post(
            formData
        )

        if (response.data?.data?.bannerUrl) {
            form.value.bannerUrl = response.data.data.bannerUrl
        }

        selectedFile.value = null

        webAlert(response.data);
    } catch (error) {
        webAlert('Błąd podczas zapisywania ustawień.');
    } finally {
        saving.value = false
    }
}

onMounted(() => {
    loadData()
})
</script>

<style scoped>
.banner-preview-container {
    width: 100%;
    max-height: 220px;
    background-color: #f8f9fa;
}

.banner-img {
    width: 100%;
    max-height: 220px;
    object-fit: cover;
}
</style>